import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import { styled, Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { PageTab } from "app/routes/network/tabs";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import IndividualProfile from "marketplace/values/individual-profile/individual-profile";
import ViewIndividualProfile from "marketplace/values/individual-profile/view/view-individual-profile";
import IndividualAvatar from "marketplace/view/individual-avatar";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as MrtRow,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import ChatDialog from "messaging/entities/forum/view/components/chat-dialog";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import {
  useLoaderData,
  useNavigate,
  useSearchParams
} from "react-router-dom";
import NetworkedGroupAPIService from "users/entities/networked-group/api/networked-group-api-service";
import CreateNetworkedGroupApiRequest from "users/entities/networked-group/api/request-contracts/create-networked-group-api-request";
import NetworkedGroupQuery, { 
  NetworkedGroupFilterParameters, 
  NetworkedGroupOrderField, 
  NetworkedGroupOrderParameters 
} from "users/entities/networked-group/api/request-contracts/query-networked-groups-api-request";
import NetworkedGroup from "users/entities/networked-group/networked-group";
import CreateNetworkedGroupComponent from "users/entities/networked-group/view/create-networked-group";
import NetworkedGroupAvatarGroup from "users/entities/networked-group/view/networked-group-avatar-group";
import EditNetworkedGroupComponent from "users/entities/networked-group/view/edit-networked-group";
import UserNetworkConnectionAPIService from "users/entities/user-network-connection/api/user-network-connection-api-service";
import NetworkedUserInfo from "users/entities/user-network-connection/networked-user-info";
import NetworkInvitationAPIService from "users/entities/user-network-invitation/api/network-invitation-api-service";
import NetworkInvitationQuery, { 
  NetworkInvitationFilterParameters, 
  NetworkInvitationOrderField, 
  NetworkInvitationOrderParameters 
} from "users/entities/user-network-invitation/api/request-contracts/query-network-invitation-api-request";
import NetworkInvitationInfo from "users/entities/user-network-invitation/network-invitation-info";
import { useSession } from "users/session/session-context";
import UserCoworkerQuery, { 
  UserCoworkerFilterParameters, 
  UserCoworkerOrderField, 
  UserCoworkerOrderParameters 
} from "users/values/user-coworker/api/request-contracts/query-user-coworker-api-request";
import UserCoworkerApiService from "users/values/user-coworker/api/user-coworker-api-service";
import UserCoworker from "users/values/user-coworker/user-coworker";
import UpdateNetworkedGroupApiRequest from "users/entities/networked-group/api/request-contracts/update-networked-group-api-request";
import { NotAuthorized } from "common/components/not-authorized";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const FilterButtons = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  "& > .MuiChip-root": {
    [theme.breakpoints.down('xl')]: {
      marginBottom: theme.spacing(0.5),
    },
    marginLeft: theme.spacing(0.5),
  }
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0
}));

export type NetworkTableDataEntry =
  | NetworkedUserInfo
  | UserCoworker
  | NetworkInvitationInfo
  | NetworkedGroup;

export default function Network() {
  const [data, setData] = React.useState<NetworkTableDataEntry[]>([]);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState(PageTab.Contacts);
  const [sent, setSent] = React.useState(false);
  const [received, setReceived] = React.useState(false);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [rowCount, setRowCount] = React.useState(0);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useSession();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.companyEntityId) return;

    let abortController = new AbortController();

    if (routeParams.tab) {
      setActiveTab(routeParams.tab as PageTab);
    } else {
      navigate(`/network/${PageTab.Contacts}`);
    }

    if (routeParams.tab == PageTab.Groups) {
      if (routeParams.action === "new") {
        handleCreateNewNetworkedGroup();
      } else if (routeParams.action === "edit" && routeParams.id) {
        handleEditNetworkedGroup(new Guid(routeParams.id));
      }
    }
    else if (routeParams.action === "message" && routeParams.id) {
      void handleSendMessageToContact(new Guid(routeParams.id));
    }
    else if (routeParams.action === "view" && routeParams.id) {
      handleViewProfile(new Guid(routeParams.id));
    }

    let context = searchParams.get("context");
    if (routeParams.tab === PageTab.Pending && context === null) {
      setSearchParams((prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("context", "received");
        return params;
      });
      return;
    }
    setSent(context === "sent");
    setReceived(context === "received");

    void fetchData(routeParams.tab as PageTab, context, abortController);

    return () => {
      abortController.abort();
    };
  }, [
    routeParams,
    searchParams,
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting
  ]);

  const fetchContactsData = async (abortController?: AbortController) => {
    const userNetworkService = new UserNetworkConnectionAPIService(session);
    let connections = await userNetworkService.getUserConnectionsInfo(abortController);
    session?.updateNetworkConnections(connections);
    setRowCount(connections.length);
    return connections;
  };

  const fetchCoworkersData = async (
    paginationParameters: PaginationParameters, 
    abortController?: AbortController
  ) => {
    const userCoworkerService = new UserCoworkerApiService(session);
    const filterParameters = new UserCoworkerFilterParameters();
    filterParameters.name = columnFilters.find((filter) => filter.id === "name")?.value as string;
    filterParameters.email = columnFilters.find((filter) => filter.id === "email")?.value as string;
    filterParameters.entityName = columnFilters.find((filter) => filter.id === "entityName")?.value as string;

    const orderParameters = new UserCoworkerOrderParameters(
      sorting[0]?.id as UserCoworkerOrderField ?? UserCoworkerOrderField.Name,
      sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending
    );

    const coworkerQuery = new UserCoworkerQuery(
      filterParameters,
      orderParameters,
      paginationParameters,
      globalFilter
    );

    const paginatedResponse = await userCoworkerService.getUserCoworkers(
      coworkerQuery,
      abortController
    );
    setRowCount(paginatedResponse.totalElements);
    return paginatedResponse.data;
  };

  const fetchPendingData = async (
    paginationParameters: PaginationParameters, 
    context: string | null, 
    abortController?: AbortController
  ) => {
    const networkInvitationService = new NetworkInvitationAPIService(session);
    const filterParameters = new NetworkInvitationFilterParameters();

    filterParameters.name = columnFilters.find((filter) => filter.id === "name")?.value as string;
    filterParameters.status = columnFilters.find((filter) => filter.id === "status")?.value as string;
    filterParameters.message = columnFilters.find((filter) => filter.id === "message")?.value as string;

    const sentDate = columnFilters.find((filter) => filter.id === "createdDate")?.value as string;
    if (sentDate) {
      const parsedDate = new Date(sentDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        filterParameters.sentDate = parsedDate;
      }
    }

    const orderParameters = new NetworkInvitationOrderParameters(
      sorting[0]?.id as NetworkInvitationOrderField ?? NetworkInvitationOrderField.Name,
      sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending
    );

    const invitationQuery = new NetworkInvitationQuery(
      filterParameters,
      orderParameters,
      paginationParameters,
      globalFilter
    );
    const paginatedResponse = await networkInvitationService.getUserNetworkInvitations(
      invitationQuery,
      abortController
    );
    const filteredData = paginatedResponse.data.filter((invitation) => {
      if (context === "sent" && invitation.inviter) return true;
      return context === "received" && !invitation.inviter;

    });
    setRowCount(paginatedResponse.totalElements);
    return filteredData;
  };

  const fetchGroupsData = async (paginationParameters: PaginationParameters, abortController?: AbortController) => {
    const networkedGroupService = new NetworkedGroupAPIService(session);
    const filterParameters = new NetworkedGroupFilterParameters();
    filterParameters.name = columnFilters.find((filter) => filter.id === "name")?.value as string;
    filterParameters.description = columnFilters.find((filter) => filter.id === "description")?.value as string;
    const orderParameters = new NetworkedGroupOrderParameters(
      sorting[0]?.id as NetworkedGroupOrderField ?? NetworkedGroupOrderField.Name,
      sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending
    );
    const paginatedResponse = await networkedGroupService.queryNetworkedGroups(
      new NetworkedGroupQuery(
        filterParameters,
        orderParameters,
        paginationParameters
      ),
      abortController
    );
    setRowCount(paginatedResponse.totalElements);
    return paginatedResponse.data;
  };

  const fetchData = async (
    tab: PageTab,
    context: string | null,
    abortController?: AbortController
  ) => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    try {
      let result: NetworkTableDataEntry[];
      const paginationParameters = new PaginationParameters(
        pagination.pageIndex,
        pagination.pageSize
      );
      const currentTab = tab ?? activeTab;

      switch (currentTab) {
        case PageTab.Contacts:
          result = await fetchContactsData(abortController);
          break;
        case PageTab.Coworkers:
          result = await fetchCoworkersData(paginationParameters, abortController);
          break;
        case PageTab.Pending:
          result = await fetchPendingData(paginationParameters, context, abortController);
          break;
        case PageTab.Groups:
          result = await fetchGroupsData(paginationParameters, abortController);
          break;
        default:
          result = [];
      }
      setData(result);
    } catch (error) {
      if (error instanceof CanceledError) return;
      setWasErrorLoading(true);
      console.error(error);
      return;
    }

    setWasErrorLoading(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  function renderContactNameColumn(user: NetworkedUserInfo | UserCoworker) {
    return (
      <NameCellContainer>
        <IndividualAvatar
          avatarId={user.avatarId}
          individualId={user.individualId}
          session={session}
        />
        <RowTitle>{user.name?.toString()}</RowTitle>
      </NameCellContainer>
    );
  }

  function renderPendingNameColumn(invitation: NetworkInvitationInfo) {
    return (
      <NameCellContainer>
        <IndividualAvatar
          avatarId={invitation.avatarId}
          individualId={invitation.individualId}
          session={session}
        />
        <RowTitle>{invitation.user?.fullName?.toString()}</RowTitle>
      </NameCellContainer>
    );
  }

  const columnHelper = createMRTColumnHelper<NetworkTableDataEntry>();

  const contactsColumns = [
    columnHelper.accessor(
      (data) =>
        renderContactNameColumn(data as NetworkedUserInfo),
      {
        header: "Name",
        id: "name",
      }
    ),
    columnHelper.accessor(
      (entry) =>
        (entry as NetworkedUserInfo)?.createdDate?.format("L"),
      {
        header: "Connected On",
        id: "createdDate",
      }
    ),
  ];

  const coworkersColumns = [
    columnHelper.accessor(
      (data) =>
        renderContactNameColumn(data as UserCoworker),
      {
        header: "Name",
        id: "name",
      }
    ),
    columnHelper.accessor((entry) => (entry as UserCoworker).entityName, {
      header: "Legal Entity",
      id: "entityName",
    }),
  ];

  const pendingColumns = [
    columnHelper.accessor(
      (data) => renderPendingNameColumn(data as NetworkInvitationInfo),
      {
        header: "Name",
        id: "name",
      }
    ),
    columnHelper.accessor(
      (entry) => {
        const created = (entry as NetworkInvitationInfo).created;
        if (created instanceof Date) {
          return created.toLocaleDateString();
        }
        return created;
      },
      {
        header: "Sent",
        id: "createdDate",
      }
    ),
    columnHelper.accessor(
      (entry) => {
        const message = (entry as NetworkInvitationInfo).message;
        return message || <Icon>remove</Icon>;
      },
      {
        header: "Message",
        id: "message",
      }
    ),
    columnHelper.accessor((entry) => (entry as NetworkInvitationInfo).status, {
      header: "Status",
      id: "status",
    }),
  ];

  const groupsColumns = [
    columnHelper.accessor((entry) => (entry as NetworkedGroup).name, {
      header: "Name",
      id: "name",
    }),
    columnHelper.accessor((entry) => (entry as NetworkedGroup).description || <Icon>remove</Icon>, {
      header: "Description",
      id: "description",
    }),
    columnHelper.accessor((entry) => {
      return <NetworkedGroupAvatarGroup
        group={(entry as NetworkedGroup)}
        session={session}
        avatarGroupMax={3}
      />;
    }, {
      header: "Members",
      id: "members"
    }),
  ];

  function handleCreateNewNetworkedGroup() {
    openDialog({
      title: "Create Networked Group",
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "md",
        fullWidth: true,
      },
      component: <CreateNetworkedGroupComponent
        onCreate={(result: CreateNetworkedGroupApiRequest | null) => {
          if (!result) {
            navigate(`/network/${PageTab.Groups}`)
          } else {
            const networkedGroupService = new NetworkedGroupAPIService(session);
            networkedGroupService.createNetworkedGroup(result).then((group) => {
              navigate(`/network/${PageTab.Groups}/edit/${group.id}`);
            }).catch((error) => {
              console.error(error);
              enqueueSnackbar("Failed to create networked group", { variant: "error" });
            });
          }
        }}
      />
    }, () => navigate(`/network/${PageTab.Groups}`));
  }

  function handleEditNetworkedGroup(groupId: Guid) {
    openDialog({
      title: "Update Networked Group",
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "md",
        fullWidth: true,
      },
      component: <EditNetworkedGroupComponent
        groupId={groupId}
        onSave={(result: UpdateNetworkedGroupApiRequest | undefined) => {
          if (!result) {
            navigate(`/network/${PageTab.Groups}`)
          } else {
            const networkedGroupService = new NetworkedGroupAPIService(session);
            networkedGroupService.updateNetworkedGroup(
              groupId,
              result).then(() => {
                navigate(`/network/${PageTab.Groups}`);
              }).catch((error) => {
                console.error(error);
                enqueueSnackbar("Failed to update networked group", { variant: "error" });
              });
          }
        }}
      />
    }, () => navigate(`/network/${PageTab.Groups}`));
  }

  async function handleAcceptInvitation(invitationId: Guid): Promise<void> {
    if (!invitationId){
      await Promise.reject(new Error("No network invitation id provided"));
      return;
    }

    try {
      const networkInvitationService = new NetworkInvitationAPIService(session);
      const connection = await networkInvitationService.acceptNetworkInvitation(
        invitationId
      );
      session.addNetworkConnection(
        connection.toProfileConnection(session.user?.id)
      );
      await fetchData(activeTab, searchParams.get("boxType"));
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to accept invitation", { variant: "error" });
    }
  }

  async function handleRejectInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId){
        await Promise.reject(new Error("No network invitation id provided"));
        return;
      }
      const networkInvitationService = new NetworkInvitationAPIService(session);
      await networkInvitationService.rejectNetworkInvitation(invitationId);
      session.removeNetworkInvitationByUser(invitationId);
      await fetchData(activeTab, searchParams.get("boxType"));
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to reject invitation", { variant: "error" });
    }
  }

  async function handleCancelInvitation(invitationId: Guid): Promise<void> {
    try {
      if (!invitationId){
        await Promise.reject(new Error("No network invitation id provided"));
        return;
      }

      const response = await confirm({
        title: "Cancel Network Connection Invitation?",
        message:
          "Are you sure you want to cancel this network connection invitation?",
        okButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (response === ConfirmResponse.Cancel) return;

      const networkInvitationService = new NetworkInvitationAPIService(session);
      await networkInvitationService.cancelNetworkInvitation(invitationId);
      session.removeNetworkInvitationByUser(invitationId);
      await fetchData(activeTab, searchParams.get("boxType"));
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel invitation", { variant: "error" });
    }
  }

  async function handleRemoveNetworkConnection(
    connectionId?: Guid
  ): Promise<void> {
    try {
      if (!connectionId) throw new Error("No network connection id provided");

      const response = await confirm({
        title: "Remove Network Connection",
        message: "Are you sure you want to remove this network connection?",
        okButtonText: "Remove",
      });

      if (response === ConfirmResponse.Cancel) return;

      const userNetworkService = new UserNetworkConnectionAPIService(session);
      await userNetworkService.removeNetworkConnection(connectionId);
      session.removeNetworkConnectionByUserId(connectionId);
      await fetchData(activeTab, searchParams.get("boxType"));
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to remove network connection", {
        variant: "error",
      });
    }
  }

  async function handleSendMessageToContact(userId?: Guid): Promise<void> {
    try {
      if (!userId) throw new Error("No user id provided");

      const userNetworkService = new UserNetworkConnectionAPIService(session);
      let forum = await userNetworkService.getChatForumForNetworkConnection(
        userId
      );

      forum ??= await userNetworkService.createChatForumForNetworkConnection(userId);

      const parentPath = new URL(
        ".",
        globalThis.location.origin + globalThis.location.pathname
      );

      openDialog(
        {
          title: "Message",
          contentSxProps: {
            display: "flex",
            overflowX: "hidden",
            paddingBottom: 0,
          },
          MuiProps: {
            fullWidth: true,
            maxWidth: "md",
          },
          component: <ChatDialog forums={[forum]} />,
        },
        () => navigate(parentPath.pathname)
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to send message", { variant: "error" });
    }
  }

  function handleViewProfile(individualId?: Guid, data?: NetworkTableDataEntry): void {
    if (!individualId) return;
    let individualProfile: IndividualProfile | undefined;
    if (data instanceof NetworkedUserInfo) {
      const profile = new IndividualProfile(data.userId, data.name.firstName ?? "", data.name.lastName ?? "", true);
      profile.avatarId = data.avatarId;
      profile.entityId = data.companyEntityId;
      profile.email = data.email;
    } else if (data instanceof NetworkInvitationInfo) {
      individualProfile = new IndividualProfile(data.user.id, data.user.fullName.firstName ?? "", data.user.fullName.lastName ?? "", true);
      individualProfile.avatarId = data.avatarId;
      individualProfile.email = data.user.email ?? undefined;
    }

    openDialog(
      {
        titleStyle: {
          position: "absolute",
          right: 0,
          top: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: false,
        },
        component: (
          <ViewIndividualProfile
            individualId={individualId}
            profile={individualProfile}
            onNetworkStatusUpdated={() => fetchData(activeTab, null)}
          />
        ),
      }
    );
  }

  function getPendingTabActions(row: MrtRow<NetworkTableDataEntry>) {
    const invitation = row.original as NetworkInvitationInfo;
    if (invitation?.status === 'accepted' || invitation?.status === 'rejected') {
      return null;
    }
    return (
      <>
        {invitation.inviter ? (
          <Tooltip title="Cancel Invitation">
            <span>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  void handleCancelInvitation(invitation.id);
                }}
              >
                <CancelIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Accept Invitation">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleAcceptInvitation(invitation.id);
                  }}
                >
                  <CheckIcon color="success" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reject Invitation">
              <span>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleRejectInvitation(invitation.id);
                  }}
                >
                  <CloseIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </>
    );
  }

  function getContactsActions(row: MrtRow<NetworkTableDataEntry>) {
    // If the user is a contact
    const connection = row.original as NetworkedUserInfo;
    return (
      <Tooltip title="Remove Connection">
        <span>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              void handleRemoveNetworkConnection(connection.connectionId);
            }}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  function getColumns() {
    switch (activeTab) {
      case PageTab.Pending:
        return pendingColumns;
      case PageTab.Coworkers:
        return coworkersColumns;
      case PageTab.Groups:
        return groupsColumns;
      default:
        return contactsColumns;
    }
  }

  function generateRowId(row: NetworkTableDataEntry) {
    if (row instanceof NetworkedGroup) {
      return row.id.toString();
    } else {
      if (!row.individualId) {
        return 'no-individual-id-row';
      }
      return row.individualId.toString();
    }
  }

  function generateClickedRoute(row: MrtRow<NetworkTableDataEntry>): string {
    return row.original instanceof NetworkedGroup ?
      `/network/${activeTab}/edit/${row.original.id}` :
      `/network/${activeTab}/view/${row.original.individualId}`;
  }

  const table = useMaterialReactTable({
    columns: getColumns(),
    data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: activeTab !== PageTab.Coworkers,
    enableColumnPinning: true,
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            {
              activeTab !== PageTab.Groups &&
              <Button startIcon={<AddIcon />} onClick={() => navigate("/marketplace")}>
                Expand Network
              </Button>
            }{
              activeTab === PageTab.Groups &&
              <Button startIcon={<AddIcon />} onClick={() => navigate(`/network/${PageTab.Groups}/new`)}>
                New Group
              </Button>
            }
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton
            color="primary"
            onClick={() => navigate("/marketplace")}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderRowActions: ({ row }) => {
      return (
        <RowActionsContainer>
          {activeTab === PageTab.Pending && getPendingTabActions(row)}
          {activeTab === PageTab.Contacts && getContactsActions(row)}
        </RowActionsContainer>
      );
    },
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Network</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => {
                navigate(`/network/${newValue}`);
              }}
            >
              <Tab value={PageTab.Contacts} label="Contacts" />
              <Tab value={PageTab.Coworkers} label="Coworkers" />
              <Tab value={PageTab.Pending} label="Pending" />
              <Tab value={PageTab.Groups} label="Groups" />
            </Tabs>
            {activeTab === PageTab.Pending && (
              <FilterButtons>
                <Chip
                  icon={<FilterListIcon />}
                  label="Received"
                  color="primary"
                  variant={received ? "filled" : "outlined"}
                  onClick={() => {
                    if (received) return;
                    setReceived((prevValue) => !prevValue);
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (!received) {
                        params.set("context", "received");
                      } else {
                        params.set("context", "sent");
                      }
                      return params;
                    });
                  }}
                />
                <Chip
                  icon={<FilterListIcon />}
                  label="Sent"
                  color="primary"
                  variant={sent ? "filled" : "outlined"}
                  onClick={() => {
                    if (sent) return;
                    setSent((prevValue) => !prevValue);
                    setSearchParams((prevParams) => {
                      const params = new URLSearchParams(prevParams);
                      if (!sent) {
                        params.set("context", "sent");
                      } else {
                        params.set("context", "received");
                      }
                      return params;
                    });
                  }}
                />
              </FilterButtons>
            )}
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => generateRowId(row),
    initialState: {
      columnPinning: {
        right: ["mrt-row-actions"],
      },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (activeTab !== PageTab.Coworkers) {
          navigate(generateClickedRoute(row));
        }
      },
      sx: { cursor: activeTab !== PageTab.Coworkers ? "pointer" : "default" },
    }),
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      globalFilter,
      columnFilters,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting
    },
  });

  if (!session.user?.companyEntityId) return <NotAuthorized />;

  return <MaterialReactTable table={table} />;
}
